import React from 'react'
import Typography from 'src/components/atoms/Typography'
import { IconMapPin, IconRuler, IconStack2, IconTree } from '@tabler/icons-react'
import ProjectThumbnail from './ProjectThumbnail'
import { Checkbox } from 'src/components/atoms/buttons/CheckBoxButton'
import { Project, ProjectViewMode } from 'src/types/projects'
import { LayerUploadStatus } from 'src/types/layer'
import useAuth from 'src/enviroment/hooks/useAuth'
import { UserRole } from 'src/types/user'
import { formatArea, cn } from 'src/enviroment/lib/utils'
import { ActionButton } from '../atoms/buttons/ActionButton'
import DeleteMyProjectDialog from '../organisms/dialogs/DeleteMyProjectDialog'
import EditMyProjectDialog from '../organisms/dialogs/EditMyProjectDialog'
import { projectRouterKeys } from 'src/api/routes/project'
import { api } from '../../api/root'
import { useQueryClient } from '@tanstack/react-query'
import TooltipWrapper from '../atoms/TooltipWrapper'

interface ProjectCardProps {
  project: Project
  mode: ProjectViewMode
  onOpen?: (project: Project) => void
  isSelected?: boolean
  onToggleSelect?: (id: string) => void
}

const ProjectCard: React.FC<ProjectCardProps> = ({ project, onOpen, mode, isSelected = false, onToggleSelect }) => {
  const { userRole } = useAuth()
  const queryClient = useQueryClient()
  const isDeleteAllowed = mode === 'tenant' && (userRole === UserRole.ADMIN || userRole === UserRole.CONTRIBUTOR)
  const isMapReady = project.layerUploadStatus === 'COMPLETED' || project.layerUploadStatus === 'ERROR'
  const isThumbnailGenerated = Boolean(project.thumbnailUrl)
  const copaSettled =
    project.copaStatus === LayerUploadStatus.COMPLETED || project.copaStatus === LayerUploadStatus.ERROR

  const isPolling = !isMapReady || !isThumbnailGenerated || !copaSettled

  const { data: statusData } = api.project.status.useQuery(project.id, isPolling)

  const leftColumnDetails = [
    {
      icon: <IconMapPin className="h-5 w-5" />,
      text: project.location,
    },
    {
      icon: <IconRuler className="h-5 w-5" />,
      text: formatArea(project.totalAreaHectares),
    },
  ]

  const rightColumnDetails = [
    {
      icon: <IconStack2 className="h-5 w-5" />,
      text: project.stage?.name || '-',
      show: !!project.stage,
    },
    {
      icon: <IconTree className="h-5 w-5" />,
      text: project.types.map((type) => type.name).join(', '),
      show: project.types.length > 0,
    },
  ].filter((detail) => detail.show !== false)

  // Update project list when status or thumbnail changes
  React.useEffect(() => {
    if (statusData) {
      const statusChanged = statusData.layerUploadStatus !== project.layerUploadStatus
      const thumbnailChanged = statusData.thumbnailUrl !== project.thumbnailUrl

      if (statusChanged || thumbnailChanged) {
        queryClient.invalidateQueries({
          predicate: (query) => {
            const queryKey = query.queryKey as unknown[]
            return Array.isArray(queryKey) && queryKey[0] === projectRouterKeys.list
          },
        })
      }
    }
  }, [statusData, project.layerUploadStatus, project.thumbnailUrl, project.id, queryClient])

  return (
    <div
      className={cn(
        'rounded-lg p-0.5 transition-all',
        isSelected ? 'bg-orange-500' : 'hover:bg-gradient-to-r hover:from-[#F5F5DC] hover:to-[#329175]',
      )}
    >
      <div className="group relative h-[320px] overflow-hidden rounded-lg bg-gray-800 transition-colors hover:bg-gray-900">
        <div className="relative h-44 w-full">
          <ProjectThumbnail
            thumbnailUrl={project.thumbnailUrl}
            layerUploadStatus={project.layerUploadStatus}
            copaStatus={project.copaStatus}
          />

          {/* Selection checkbox — top-left, visible on hover or when selected */}
          {onToggleSelect && (
            <div
              className={cn(
                'absolute left-2 top-2 z-10 transition-opacity',
                isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
              )}
              onClick={(e) => e.stopPropagation()}
            >
              <Checkbox
                checked={isSelected}
                onCheckedChange={() => onToggleSelect(project.id)}
                aria-label={`Select ${project.name}`}
                className="border-orange-500 data-[state=checked]:bg-orange-500 data-[state=checked]:text-gray-900"
              />
            </div>
          )}

          {/* Delete Dialog - will be shown only on hover via CSS */}
          {isDeleteAllowed && (
            <div className="absolute right-2 top-2 z-10 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
              <div className="flex flex-col space-y-2">
                <DeleteMyProjectDialog projectId={project.id} projectName={project.name} />
                <EditMyProjectDialog project={project} />
              </div>
            </div>
          )}
        </div>

        <div className="flex min-h-0 flex-1 flex-col space-y-4 p-4">
          <div className="flex flex-row items-center gap-2">
            <Typography variant="base-bold" className="min-w-0 flex-1 truncate text-light-green-50">
              {project.name}
            </Typography>
            <ActionButton onClick={() => onOpen?.(project)} />
          </div>

          <div className="flex min-h-0 flex-1 gap-4">
            {/* Left Column */}
            <div className="flex-1 space-y-4">
              {leftColumnDetails.map((detail, index) => (
                <div key={index} className="flex min-w-0 items-center gap-2 text-light-green-50">
                  <div className="flex-shrink-0">{detail.icon}</div>
                  <TooltipWrapper content={detail.text} side="bottom" delayDuration={500}>
                    <div className="w-0 min-w-0 flex-1 grow overflow-hidden">
                      <Typography variant="sm" className="block truncate">
                        {detail.text}
                      </Typography>
                    </div>
                  </TooltipWrapper>
                </div>
              ))}
            </div>

            {/* Right Column */}
            <div className="flex-1 space-y-4">
              {rightColumnDetails.map((detail, index) => (
                <div key={index} className="flex min-h-0 items-start gap-2 text-light-green-50">
                  <div className="flex-shrink-0">{detail.icon}</div>
                  <TooltipWrapper content={detail.text} side="bottom" delayDuration={500}>
                    <div>
                      <Typography variant="sm" className="line-clamp-2 capitalize">
                        {detail.text}
                      </Typography>
                    </div>
                  </TooltipWrapper>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProjectCard
